/**
 * One renderer for every chart the analytics toggle produces.
 *
 * It draws whatever spec the server chose (type / x / y / series / agg / sort /
 * top_n) instead of guessing from the column count, which is what the four
 * One-, Two-, Three- and MultiColumnComponent files used to do - and why a
 * result with four or more columns never got a chart at all.
 *
 * Scaling is this file's other job: bars measured from zero, lines padded
 * around their own range, compact axis ticks with full values in the tooltip,
 * rotated labels with margins sized to fit them, and a log axis when the
 * values really do span orders of magnitude.
 */
import React, { useMemo } from 'react';
import { useTheme } from '@mui/material/styles';
import { Box, Typography, Card, CardContent, Grid, Alert } from '@mui/material';
import {
  BarChart, Bar, LineChart, Line, AreaChart, Area, ScatterChart, Scatter,
  PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, LabelList,
} from 'recharts';
import {
  aggregateForSpec, computeDomain, shouldUseLogScale, makeValueFormatter,
  labelMetrics, parseNumber,
} from '../utils/chartData';

const PALETTE = [
  '#4e79a7', '#f28e2b', '#59a14f', '#e15759', '#76b7b2',
  '#edc948', '#b07aa1', '#ff9da7', '#9c755f', '#bab0ac',
];

const prettify = (name) => String(name || '').replace(/_/g, ' ');

/** Bars and columns of text need very different amounts of room. */
function resolveType(spec, metrics) {
  const type = spec.type;
  if (type === 'bar' && (metrics.count > 10 || metrics.longest > 16)) return 'horizontal_bar';
  if ((type === 'pie' || type === 'donut') && metrics.count > 8) return 'horizontal_bar';
  return type;
}

function ChartFrame({ title, subtitle, children, height }) {
  return (
    <Box sx={{ width: '100%' }}>
      {title && (
        <Typography variant="subtitle1" align="center" sx={{ fontWeight: 600, mb: 0.5 }}>
          {title}
        </Typography>
      )}
      {subtitle && (
        <Typography variant="caption" align="center" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          {subtitle}
        </Typography>
      )}
      <ResponsiveContainer width="100%" height={height}>
        {children}
      </ResponsiveContainer>
    </Box>
  );
}

const SmartChart = ({ spec, rows, height = 380, showTitle = true }) => {
  const theme = useTheme();
  const axisColor = theme.palette.text.secondary;
  const gridColor = theme.palette.divider;

  const { data, seriesKeys, xIsDate, otherCount, xKey } = useMemo(
    () => aggregateForSpec(rows, spec || {}),
    [rows, spec],
  );

  const format = useMemo(() => makeValueFormatter(spec || {}), [spec]);

  const flatValues = useMemo(() => {
    const values = [];
    data.forEach((entry) => {
      seriesKeys.forEach((key) => {
        const value = parseNumber(entry[key]);
        if (value !== null) values.push(value);
      });
    });
    return values;
  }, [data, seriesKeys]);

  if (!spec || !spec.type || spec.type === 'table') return null;

  if (spec.type === 'kpi') {
    const measures = (spec.y || []).filter(Boolean);
    const totals = measures.map((measure) => {
      const values = (rows || []).map((row) => parseNumber(row[measure])).filter((v) => v !== null);
      if (!values.length) return { measure, value: null };
      const sum = values.reduce((a, b) => a + b, 0);
      return { measure, value: spec.agg === 'avg' ? sum / values.length : sum };
    });
    return (
      <Box>
        {showTitle && spec.title && (
          <Typography variant="subtitle1" align="center" sx={{ fontWeight: 600, mb: 1.5 }}>
            {spec.title}
          </Typography>
        )}
        <Grid container spacing={2} justifyContent="center">
          {totals.map(({ measure, value }) => (
            <Grid item xs={12} sm={6} md={4} key={measure}>
              <Card variant="outlined">
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" color="primary" sx={{ fontWeight: 600 }}>
                    {value === null ? '-' : format.full(value)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {prettify(measure)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  if (!data.length || !seriesKeys.length) {
    return <Alert severity="info">There is nothing to plot in this result.</Alert>;
  }

  const metrics = labelMetrics(data, xKey);
  const type = resolveType(spec, metrics);
  const title = showTitle ? (spec.title || `${prettify((spec.y || [])[0] || 'Count')} by ${prettify(spec.x)}`) : null;
  const subtitle = otherCount
    ? `Top ${spec.top_n || 12} shown; the remaining ${otherCount} grouped as Other`
    : null;

  // Axis furniture sized to the text that will actually sit in it, so long
  // labels are not clipped and short ones do not leave a gutter.
  const widestTick = Math.max(
    ...flatValues.map((value) => format.compact(value).length),
    3,
  );
  const yAxisWidth = Math.min(110, widestTick * 8 + 16);
  const rotateLabels = metrics.longest > 6 && metrics.count > 4;
  const bottomMargin = rotateLabels ? Math.min(110, metrics.longest * 5 + 30) : 20;

  const useLog = (type === 'line' || type === 'area' || type === 'scatter') && shouldUseLogScale(flatValues);
  const zeroBase = type !== 'line' && type !== 'area' && type !== 'scatter';
  const domain = useLog ? ['auto', 'auto'] : computeDomain(flatValues, { zeroBase });

  const commonAxisProps = {
    tick: { fill: axisColor, fontSize: 12 },
    axisLine: { stroke: gridColor },
    tickLine: { stroke: gridColor },
  };

  const tooltip = (
    <Tooltip
      formatter={(value, name) => [format.full(value), prettify(name)]}
      contentStyle={{
        backgroundColor: theme.palette.background.paper,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 6,
        color: theme.palette.text.primary,
      }}
    />
  );
  const legend = seriesKeys.length > 1
    ? <Legend formatter={(value) => prettify(value)} wrapperStyle={{ fontSize: 12 }} />
    : null;

  if (type === 'pie' || type === 'donut') {
    const key = seriesKeys[0];
    const pieData = data.map((entry) => ({ name: String(entry[xKey]), value: parseNumber(entry[key]) || 0 }));
    return (
      <ChartFrame title={title} subtitle={subtitle} height={height}>
        <PieChart>
          <Pie
            data={pieData}
            dataKey="value"
            nameKey="name"
            cx="50%"
            cy="50%"
            innerRadius={type === 'donut' ? '45%' : 0}
            outerRadius="75%"
            labelLine={false}
            label={({ name, percent }) => (percent > 0.04 ? `${name} ${(percent * 100).toFixed(0)}%` : '')}
          >
            {pieData.map((entry, index) => (
              <Cell key={entry.name} fill={PALETTE[index % PALETTE.length]} />
            ))}
          </Pie>
          <Tooltip formatter={(value, name) => [format.full(value), name]} />
        </PieChart>
      </ChartFrame>
    );
  }

  if (type === 'scatter') {
    const yKey = seriesKeys[0];
    return (
      <ChartFrame title={title} subtitle={subtitle} height={height}>
        <ScatterChart margin={{ top: 10, right: 24, bottom: 30, left: 8 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
          <XAxis
            type="number"
            dataKey={spec.x}
            name={prettify(spec.x)}
            tickFormatter={format.compact}
            domain={computeDomain(data.map((entry) => entry[spec.x]), { zeroBase: false })}
            label={{ value: spec.x_label || prettify(spec.x), position: 'insideBottom', offset: -18, fill: axisColor, fontSize: 12 }}
            {...commonAxisProps}
          />
          <YAxis
            type="number"
            dataKey={yKey}
            name={prettify(yKey)}
            width={yAxisWidth}
            scale={useLog ? 'log' : 'auto'}
            domain={domain}
            tickFormatter={format.compact}
            {...commonAxisProps}
          />
          {tooltip}
          <Scatter data={data} fill={PALETTE[0]} />
        </ScatterChart>
      </ChartFrame>
    );
  }

  if (type === 'line' || type === 'area') {
    const Chart = type === 'area' ? AreaChart : LineChart;
    return (
      <ChartFrame title={title} subtitle={subtitle} height={height}>
        <Chart data={data} margin={{ top: 10, right: 24, bottom: rotateLabels ? bottomMargin - 20 : 8, left: 8 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={gridColor} vertical={false} />
          <XAxis
            dataKey={xKey}
            interval="preserveStartEnd"
            minTickGap={24}
            angle={rotateLabels && !xIsDate ? -35 : 0}
            textAnchor={rotateLabels && !xIsDate ? 'end' : 'middle'}
            height={rotateLabels && !xIsDate ? bottomMargin : 30}
            {...commonAxisProps}
          />
          <YAxis
            width={yAxisWidth}
            scale={useLog ? 'log' : 'auto'}
            domain={domain}
            allowDataOverflow={false}
            tickFormatter={format.compact}
            {...commonAxisProps}
          />
          {tooltip}
          {legend}
          {seriesKeys.map((key, index) => (type === 'area' ? (
            <Area
              key={key}
              type="monotone"
              dataKey={key}
              stackId={spec.series ? 'stack' : undefined}
              stroke={PALETTE[index % PALETTE.length]}
              fill={PALETTE[index % PALETTE.length]}
              fillOpacity={0.25}
              strokeWidth={2}
              connectNulls
            />
          ) : (
            <Line
              key={key}
              type="monotone"
              dataKey={key}
              stroke={PALETTE[index % PALETTE.length]}
              strokeWidth={2}
              dot={data.length <= 30}
              activeDot={{ r: 5 }}
              connectNulls
            />
          )))}
        </Chart>
      </ChartFrame>
    );
  }

  const stacked = type === 'stacked_bar';
  const horizontal = type === 'horizontal_bar';
  // A horizontal chart grows downwards instead of squeezing bars together.
  const barHeight = horizontal ? Math.max(height, data.length * 30 + 70) : height;
  const categoryWidth = horizontal ? Math.min(220, Math.max(70, metrics.longest * 7)) : undefined;
  const showBarLabels = !horizontal && seriesKeys.length === 1 && data.length <= 12;

  return (
    <ChartFrame title={title} subtitle={subtitle} height={barHeight}>
      <BarChart
        data={data}
        layout={horizontal ? 'vertical' : 'horizontal'}
        margin={{
          top: showBarLabels ? 22 : 10,
          right: 24,
          bottom: horizontal ? 12 : (rotateLabels ? bottomMargin - 20 : 8),
          left: 8,
        }}
        barCategoryGap={horizontal ? '20%' : '15%'}
      >
        <CartesianGrid strokeDasharray="3 3" stroke={gridColor} horizontal={!horizontal} vertical={horizontal} />
        {/* Recharts reads its axes straight off the child list, so these stay
            two plain elements rather than a fragment per orientation. */}
        <XAxis
          {...(horizontal
            ? { type: 'number', domain, tickFormatter: format.compact }
            : {
              type: 'category',
              dataKey: xKey,
              interval: 0,
              angle: rotateLabels ? -35 : 0,
              textAnchor: rotateLabels ? 'end' : 'middle',
              height: rotateLabels ? bottomMargin : 30,
            })}
          {...commonAxisProps}
        />
        <YAxis
          {...(horizontal
            ? { type: 'category', dataKey: xKey, width: categoryWidth }
            : { type: 'number', width: yAxisWidth, domain, tickFormatter: format.compact })}
          {...commonAxisProps}
        />
        {tooltip}
        {legend}
        {seriesKeys.map((key, index) => (
          <Bar
            key={key}
            dataKey={key}
            stackId={stacked ? 'stack' : undefined}
            fill={PALETTE[index % PALETTE.length]}
            radius={horizontal ? [0, 3, 3, 0] : [3, 3, 0, 0]}
            maxBarSize={horizontal ? 26 : 64}
          >
            {showBarLabels && (
              <LabelList
                dataKey={key}
                position="top"
                formatter={format.compact}
                style={{ fill: axisColor, fontSize: 11 }}
              />
            )}
          </Bar>
        ))}
      </BarChart>
    </ChartFrame>
  );
};

export default SmartChart;
